import { client } from '@/lib/apollo-client';
import {
  FRAMEIO_SHARE_VIDEOS,
  type FrameioShareVideo,
  type FrameioShareVideos,
  type FrameioShareVideosQueryVariables,
} from '@/queries/frameio';
import type { VideoMetadata } from '@/lib/video-metadata';
import { getFrameioErrorCode } from './errors';
import { parseFrameioUrl } from './links';

// `frameioShareVideos` is left as `unknown` here (rather than the strict
// FrameioShareVideosQuery type) so a caller-supplied result — including a
// deliberately malformed one in tests — is always validated at runtime via
// isShareVideos()/isVideo() below rather than trusted from the type system.
type QueryFn = (options: {
  query: typeof FRAMEIO_SHARE_VIDEOS;
  variables: FrameioShareVideosQueryVariables;
  fetchPolicy: 'no-cache';
  errorPolicy: 'all';
}) => Promise<{ data?: { frameioShareVideos?: unknown } | null; error?: unknown; errors?: readonly unknown[] }>;

function isVideo(value: unknown): value is FrameioShareVideo {
  const v = value as FrameioShareVideo;
  return Boolean(v) && typeof v.asset_id === 'string' && typeof v.name === 'string'
    && typeof v.canonical_url === 'string' && Number.isFinite(v.duration_seconds) && v.duration_seconds > 0;
}

export function isShareVideos(value: unknown): value is FrameioShareVideos {
  const share = value as FrameioShareVideos;
  return Boolean(share) && typeof share.truncated === 'boolean'
    && Array.isArray(share.videos) && share.videos.length > 0 && share.videos.every(isVideo);
}

export async function getFrameioShareVideos(
  url: string,
  { query = client.query.bind(client) as unknown as QueryFn }: { query?: QueryFn } = {},
): Promise<VideoMetadata> {
  if (parseFrameioUrl(url).kind !== 'share') throw new Error('FRAMEIO_UNSUPPORTED_LINK');
  let result;
  try {
    result = await query({
      query: FRAMEIO_SHARE_VIDEOS, variables: { url }, fetchPolicy: 'no-cache', errorPolicy: 'all',
    });
  } catch (error) {
    throw new Error(getFrameioErrorCode(error) ?? 'FRAMEIO_UNAVAILABLE');
  }
  if (result.error || result.errors?.length) throw new Error(getFrameioErrorCode(result) ?? 'FRAMEIO_UNAVAILABLE');
  const share = result.data?.frameioShareVideos;
  if (!isShareVideos(share)) throw new Error('FRAMEIO_UNAVAILABLE');
  return {
    platform: 'frameio',
    url,
    title: share.share_name ?? '',
    duration: Math.max(...share.videos.map((video) => video.duration_seconds)),
    live_status: 'not_live',
    shareVideos: share,
  };
}
